/**
 * SaveController — orchestrates the save/load lifecycle on top of DexieSaveAdapter.
 *
 * # What this file owns
 *
 * 1. **Boot sequence** — open adapter, load latest save, dispatch SaveAction
 *    so the gameStore reducer can apply it (Layer 5 will add the reducer).
 * 2. **Autosave debounce** — every dispatched gameplay action resets a 1s
 *    timer; when the timer fires, save runs once.
 * 3. **Force-flush** — on `visibilitychange` (tab hidden) and `beforeunload`
 *    (tab close), cancel the timer and save immediately.
 * 4. **Install ID lifecycle** — generate a UUIDv4 lazily on first save,
 *    persist in the adapter's `meta` store. Same getOrCreate pattern as
 *    install secret. Different key.
 * 5. **Error classification** — translate adapter LoadResult / write
 *    exceptions into Phase A's SaveLoadFailureReason / SaveWriteFailureReason
 *    enums.
 *
 * # What this file does NOT own
 *
 * - **The store shape** — gameStore stays at `GameState` for Phase B
 *   (per Decision: Option A). Controller wraps `state` in a synthetic
 *   `LocalState` (`{ gameState: state, actionLog: [], uiPrefs: {} }`)
 *   at save time. Phase C will introduce real actionLog tracking and
 *   may expand the store shape.
 * - **Reducer cases** — Phase A defined SaveAction types but explicitly
 *   deferred reducer cases to Phase B. Layer 5 wires the reducer to
 *   actually do something with dispatched SaveActions (replace state on
 *   SAVE_LOAD_SUCCEEDED, etc.). Layer 4 just dispatches.
 * - **UI** — banners, modals, recovery prompts are downstream concerns.
 *   This controller surfaces facts via dispatch; UI subscribes and reacts.
 *
 * # Decision references (Phase B locked decisions)
 *
 * - **Decision 1** — 3 rolling backups: handled by DexieSaveAdapter.
 * - **Decision 2** — 1s autosave debounce + force-flush on
 *   visibilitychange/beforeunload: implemented here.
 * - **Decision 3** — silent cascade through backups; banner only on all-fail;
 *   modal only on cross-install/version-too-new: the cascade IS in the
 *   adapter; this controller dispatches the appropriate SAVE_LOAD_FAILED
 *   reasons and Layer 5 / UI decides banner vs modal.
 * - **Decision 4** — separate `meta` object store; lazy generation: install
 *   secret already follows this; install ID does too (this file).
 *
 * # Lifecycle contract
 *
 *   1. Construct with adapter, dispatch fn, state-getter fn
 *   2. Call `await boot()` once at app startup. This:
 *      - opens the adapter (which generates install secret if needed)
 *      - loads the install ID (or creates one if first launch)
 *      - dispatches SAVE_LOAD_REQUESTED
 *      - calls adapter.loadLatest()
 *      - dispatches SAVE_LOAD_SUCCEEDED or SAVE_LOAD_FAILED
 *      - attaches browser-event listeners
 *   3. Call `notifyAction()` after every gameplay action dispatch.
 *      This (re)starts the 1s debounce timer.
 *   4. Call `forceFlush()` if you need to persist immediately (rare in
 *      production; mostly the browser-event handlers do this).
 *   5. Call `dispose()` on shutdown. Cancels timer, removes listeners,
 *      closes adapter.
 *
 * # Why the controller is a class, not a hook
 *
 * Hooks would couple this to React's lifecycle (re-renders, strict-mode
 * double-mount). The controller is a long-lived singleton owned by the
 * app; it lives outside React and is wired in once at boot. Layer 5 adds
 * a tiny React hook that calls `boot()` on mount and `dispose()` on unmount,
 * but the controller itself is React-agnostic and fully testable in Node.
 *
 * @see {@link ./dexieAdapter.ts} — adapter the controller orchestrates
 * @see {@link "@aevons-wake/core/actions/save"} — SaveAction variants dispatched
 * @see {@link ./browserHmacProvider.ts} — HMAC provider injected into adapter
 */

import {
  envelopeToJson,
  IntegrityError,
  SerializeError,
  type Action,
  type GameState,
  type LocalState,
  type SaveLoadFailureReason,
  type SaveWriteFailureReason,
} from '@aevons-wake/core';
import {
  type DexieSaveAdapter,
  type LoadResult,
} from './dexieAdapter';
import type { Table } from 'dexie';
import type { MetaRow } from './installSecret';

/** Autosave debounce window. Decision 2 of Phase B. */
export const AUTOSAVE_DEBOUNCE_MS = 1000;

/** Meta store key for the install ID. */
const INSTALL_ID_KEY = 'installId';

/** Function signature for dispatching actions into gameStore. */
export type DispatchFn = (action: Action) => void;

/** Function signature for snapshotting current GameState. */
export type GetStateFn = () => GameState;

/**
 * Constructor options for SaveController.
 *
 * `metaTable` is exposed separately from the adapter because the controller
 * needs to read/write the install ID directly (the adapter doesn't have
 * a generic meta-table API surface, only the install-secret-specific one).
 */
export interface SaveControllerOptions {
  adapter: DexieSaveAdapter;
  dispatch: DispatchFn;
  getState: GetStateFn;
  metaTable: Table<MetaRow, string>;
  /**
   * Optional override for the debounce window. Tests use 0 or small
   * values for fast iteration. Defaults to 1000ms.
   */
  debounceMs?: number;
  /**
   * Optional override for the visibilitychange/beforeunload listeners.
   * Tests pass `null` to skip browser-event registration. Production
   * passes nothing (defaults to `globalThis` if it has document/window).
   */
  attachBrowserListeners?: boolean;
}

/**
 * SaveController — see file-level JSDoc for full design.
 */
export class SaveController {
  private readonly adapter: DexieSaveAdapter;
  private readonly dispatch: DispatchFn;
  private readonly getState: GetStateFn;
  private readonly metaTable: Table<MetaRow, string>;
  private readonly debounceMs: number;
  private readonly attachBrowserListeners: boolean;

  private booted = false;
  private disposed = false;
  private installId: string | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private inFlightSave: Promise<void> | null = null;

  /** Bound handlers so we can remove them on dispose. */
  private readonly visibilityHandler: () => void;
  private readonly beforeunloadHandler: () => void;

  constructor(options: SaveControllerOptions) {
    this.adapter = options.adapter;
    this.dispatch = options.dispatch;
    this.getState = options.getState;
    this.metaTable = options.metaTable;
    this.debounceMs = options.debounceMs ?? AUTOSAVE_DEBOUNCE_MS;
    this.attachBrowserListeners = options.attachBrowserListeners ?? true;

    this.visibilityHandler = () => {
      // Per spec, document.hidden flips to true when the tab goes background.
      // Save immediately; we may not get another chance before the tab is
      // discarded by mobile browsers reclaiming memory.
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden'
      ) {
        void this.forceFlush();
      }
    };

    this.beforeunloadHandler = () => {
      // beforeunload is best-effort — the browser does not reliably await
      // async work during unload. We kick off the save synchronously; the
      // IndexedDB transaction will commit if the tab survives long enough.
      // If not, the in-flight save is lost but the previous-cycle save is
      // still on disk. Worst case: ~1s of pending input lost. Acceptable
      // per Hard Rule #7 (player never loses earned progress).
      void this.forceFlush();
    };
  }

  /**
   * Boot sequence. Idempotent; calling twice is a no-op after the first
   * successful boot.
   *
   * On boot:
   *   1. Dispatch SAVE_LOAD_REQUESTED
   *   2. Open adapter (catches storage_unavailable here)
   *   3. Load install ID (or generate)
   *   4. Run loadLatest cascade
   *   5. Dispatch SAVE_LOAD_SUCCEEDED or SAVE_LOAD_FAILED
   *   6. Attach browser-event listeners (if enabled)
   */
  async boot(): Promise<void> {
    if (this.booted) return;

    this.dispatch({ type: 'SAVE_LOAD_REQUESTED' });

    try {
      await this.adapter.open();
    } catch (error) {
      this.dispatch({
        type: 'SAVE_LOAD_FAILED',
        reason: 'storage_unavailable',
        message: this.errorMessage(error),
      });
      this.booted = true; // mark booted even on failure so retry doesn't loop
      return;
    }

    // Load or create install ID. This is independent of the load cascade —
    // even if no save exists yet, we need an install ID for the FIRST save
    // to attach to. Generated lazily here on first boot.
    try {
      this.installId = await this.ensureInstallId();
    } catch (error) {
      this.dispatch({
        type: 'SAVE_LOAD_FAILED',
        reason: 'storage_unavailable',
        message: `install ID lifecycle: ${this.errorMessage(error)}`,
      });
      this.booted = true;
      return;
    }

    // Run the load cascade.
    let loadResult: LoadResult;
    try {
      loadResult = await this.adapter.loadLatest();
    } catch (error) {
      // Adapter's loadLatest itself shouldn't throw under normal conditions
      // (it returns a tagged failure for everything we anticipate), but
      // catch any IndexedDB-level surprise here.
      this.dispatch({
        type: 'SAVE_LOAD_FAILED',
        reason: 'storage_unavailable',
        message: `loadLatest threw: ${this.errorMessage(error)}`,
      });
      this.booted = true;
      this.attachListenersIfEnabled();
      return;
    }

    this.dispatchLoadResult(loadResult);

    this.booted = true;
    this.attachListenersIfEnabled();
  }

  /**
   * Notify the controller that a gameplay action was dispatched.
   * (Re)starts the autosave debounce timer.
   *
   * Safe to call before boot() — debounce will fire harmlessly because
   * adapter operations check open status.
   */
  notifyAction(): void {
    if (this.disposed) return;

    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.runSave();
    }, this.debounceMs);
  }

  /**
   * Force an immediate save, bypassing the debounce.
   *
   * Returns a Promise that resolves when the save completes (or rejects
   * if the save throws). Useful for tests; production calls this from the
   * visibilitychange/beforeunload handlers.
   *
   * Coalesces with any in-flight save: if a save is already running, the
   * returned Promise resolves when that save finishes (no double-write).
   */
  async forceFlush(): Promise<void> {
    if (this.disposed) return;

    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    if (this.inFlightSave !== null) {
      // Already saving — coalesce. Wait for it.
      await this.inFlightSave;
      return;
    }

    await this.runSave();
  }

  /**
   * Cancel pending work and detach listeners. Idempotent.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    this.detachListeners();
    this.adapter.close();
  }

  /**
   * Test/diagnostic utility: get the resolved install ID after boot.
   * Returns null if boot hasn't completed or failed.
   */
  _getInstallIdForTests(): string | null {
    return this.installId;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Internal helpers
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Lazily load or generate the install ID. Mirrors the install-secret
   * getOrCreate pattern in installSecret.ts but for a different key.
   *
   * Race-safe via Dexie transaction.
   */
  private async ensureInstallId(): Promise<string> {
    return this.metaTable.db.transaction('rw', this.metaTable, async () => {
      const existing = await this.metaTable.get(INSTALL_ID_KEY);
      if (existing) {
        // installId is stored as UTF-8 bytes; decode back to UUID string.
        return new TextDecoder().decode(existing.value);
      }
      const fresh = generateInstallId();
      await this.metaTable.put({
        key: INSTALL_ID_KEY,
        value: new TextEncoder().encode(fresh),
      });
      return fresh;
    });
  }

  /** Map LoadResult to the appropriate SaveAction dispatch. */
  private dispatchLoadResult(result: LoadResult): void {
    switch (result.kind) {
      case 'loaded':
      case 'loaded-from-backup': {
        // Both successes dispatch the same action. The slot-used info is
        // available on result.slotUsed if a future UI surface wants the
        // "recovered from backup" banner; for now Layer 4 doesn't add a
        // distinct action variant.
        const json = envelopeToJson(result.envelope);
        this.dispatch({
          type: 'SAVE_LOAD_SUCCEEDED',
          envelopeJson: json,
        });
        return;
      }
      case 'not-found':
        this.dispatch({
          type: 'SAVE_LOAD_FAILED',
          reason: 'not_found',
          message: 'No save found',
        });
        return;
      case 'corrupt-all':
        this.dispatch({
          type: 'SAVE_LOAD_FAILED',
          reason: 'corrupt',
          message: this.errorMessage(result.lastError),
        });
        return;
      case 'cross-install':
        this.dispatch({
          type: 'SAVE_LOAD_FAILED',
          reason: 'tamper_detected',
          message: this.errorMessage(result.lastError),
        });
        return;
      case 'version-too-new':
        this.dispatch({
          type: 'SAVE_LOAD_FAILED',
          reason: 'version_too_new',
          message: this.errorMessage(result.lastError),
        });
        return;
    }
  }

  /**
   * Internal save runner. Wraps the adapter.save call with:
   *   - Synthetic LocalState construction (per Option A — Phase B store
   *     stays at GameState; controller wraps at save time)
   *   - SAVE_WRITE_REQUESTED dispatch before
   *   - SAVE_WRITE_SUCCEEDED or SAVE_WRITE_FAILED dispatch after
   *   - In-flight tracking so forceFlush can coalesce
   */
  private async runSave(): Promise<void> {
    if (this.disposed) return;
    if (this.installId === null) {
      // Pre-boot or boot failed. Skip silently.
      return;
    }

    this.dispatch({ type: 'SAVE_WRITE_REQUESTED' });

    const promise = this.doSave();
    this.inFlightSave = promise;
    try {
      await promise;
    } finally {
      this.inFlightSave = null;
    }
  }

  private async doSave(): Promise<void> {
    if (this.installId === null) return;

    try {
      const gameState = this.getState();
      const localState: LocalState = {
        gameState,
        actionLog: [],
        uiPrefs: {},
      };

      const envelope = await this.adapter.save(localState, this.installId);
      const envelopeJson = envelopeToJson(envelope);
      const bytes = new TextEncoder().encode(envelopeJson).length;

      this.dispatch({
        type: 'SAVE_WRITE_SUCCEEDED',
        envelopeJson,
        bytes,
      });
    } catch (error) {
      this.dispatch({
        type: 'SAVE_WRITE_FAILED',
        reason: this.classifyWriteError(error),
        message: this.errorMessage(error),
      });
    }
  }

  /** Map an exception to a SaveWriteFailureReason. */
  private classifyWriteError(error: unknown): SaveWriteFailureReason {
    // QuotaExceededError surfaces as a DOMException with that name.
    if (
      typeof DOMException !== 'undefined' &&
      error instanceof DOMException &&
      error.name === 'QuotaExceededError'
    ) {
      return 'quota_exceeded';
    }
    // Some browsers also throw plain Errors with that name.
    if (
      error instanceof Error &&
      (error.name === 'QuotaExceededError' ||
        /quota/i.test(error.message))
    ) {
      return 'quota_exceeded';
    }
    if (error instanceof SerializeError || error instanceof IntegrityError) {
      return 'serialization_failed';
    }
    return 'unknown';
  }

  private errorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    return String(error);
  }

  private attachListenersIfEnabled(): void {
    if (!this.attachBrowserListeners) return;
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.beforeunloadHandler);
    }
  }

  private detachListeners(): void {
    if (!this.attachBrowserListeners) return;
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.beforeunloadHandler);
    }
  }
}

/**
 * Generate a UUIDv4 install ID via the Web Crypto API.
 *
 * Uses `crypto.randomUUID`, available in every modern browser and Node 19+.
 * Output format matches Phase A's schema validator (`z.string().uuid()`).
 */
function generateInstallId(): string {
  return crypto.randomUUID();
}